import {
  CountrySelect,
  InputDatePicker,
  ResidentSelect
} from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import { InputParsedNumber } from '@shared/components'
import { Col, Form, Input, Row } from 'antd'
import { NamePath } from 'antd/es/form/interface'
import dayjs, { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'

import { Person } from './types'

type Props = {
  minAge?: number
  minExperience?: number
  experienceDependencies?: NamePath[]
  isEditable?: boolean
  contractorKey?: string
  personsKey?: string
  showBirthDate?: boolean
  showExperience?: boolean
  showResident?: boolean
  disableBirthDate?: boolean
  disableExperience?: boolean

  onChangeBirthDate?: (value?: Dayjs) => void
  onChangeExperience?: (value?: number | null) => void
}

export const ContractorPerson = ({
  minAge,
  minExperience = 1,
  experienceDependencies,
  isEditable = false,
  contractorKey = 'contractor',
  personsKey = 'persons',
  showBirthDate,
  showExperience,
  showResident,
  disableBirthDate,
  disableExperience,

  onChangeBirthDate,
  onChangeExperience
}: Props) => {
  const { t } = useTranslation()
  const { fullName, required, minLength, personalCode, olderThan } =
    useValidationsField()

  const formInstance = Form.useFormInstance()

  const resident = Form.useWatch([contractorKey, 'person', 'resident'])
  const country = Form.useWatch([contractorKey, 'person', 'country'])

  return (
    <Row gutter={12} align="bottom">
      {showResident && (
        <Col span={24}>
          <Form.Item
            name={[contractorKey, 'person', 'resident']}
            label={t('status')}
          >
            <ResidentSelect
              placeholder={t('status')}
              disabled={isEditable}
              countryCode={country}
            />
          </Form.Item>
        </Col>
      )}

      <Col xs={24} lg={12}>
        <Form.Item
          name={[contractorKey, 'person', 'last_name']}
          label={t('last_name')}
          rules={[required, fullName]}
        >
          <Input disabled={isEditable} placeholder={t('last_name')} />
        </Form.Item>
      </Col>

      <Col xs={24} lg={12}>
        <Form.Item
          name={[contractorKey, 'person', 'first_name']}
          label={t('first_name')}
          rules={[required, fullName]}
        >
          <Input disabled={isEditable} placeholder={t('first_name')} />
        </Form.Item>
      </Col>

      <Col
        xs={24}
        lg={24}
        {...(showResident && !resident && { lg: 18 })}
        {...(showBirthDate && { lg: 18 })}
        {...(showExperience && { lg: 20 })}
        {...(showExperience && showBirthDate && { lg: 14 })}
      >
        <Form.Item
          name={[contractorKey, 'person', 'identification_number']}
          label={t('identification_number')}
          rules={[
            required,
            resident ? personalCode : minLength,
            {
              validator: (_, value) => {
                const persons = formInstance.getFieldValue(
                  personsKey
                ) as Person[]

                const duplicateExists =
                  persons?.filter((person) => {
                    if (!person?.identification_number) return false
                    const otherIdnp = String(
                      person?.identification_number
                    ).trim()

                    return otherIdnp === value
                  }).length >= 1

                if (duplicateExists)
                  return Promise.reject(t('duplicate_idnp_error'))

                return Promise.resolve()
              }
            }
          ]}
        >
          <Input
            disabled={isEditable}
            placeholder={t('identification_number')}
          />
        </Form.Item>
      </Col>

      {showResident && !resident && (
        <Col xs={24} lg={6}>
          <Form.Item
            name={[contractorKey, 'person', 'country']}
            label={t('country_of_residence')}
            rules={[required]}
          >
            <CountrySelect
              placeholder={t('country_of_residence')}
              resident={resident}
              disabled={isEditable}
            />
          </Form.Item>
        </Col>
      )}

      {showBirthDate && (
        <Col xs={24} lg={6}>
          <Form.Item
            name={[contractorKey, 'person', 'date_of_birth']}
            label={t('date_of_birth')}
            rules={[required, olderThan?.(minAge)]}
          >
            <InputDatePicker
              placeholder={t('date_of_birth')}
              maxDate={dayjs?.()?.subtract?.(minAge || 0, 'year')}
              onChange={onChangeBirthDate}
              disabled={isEditable || disableBirthDate}
            />
          </Form.Item>
        </Col>
      )}

      {showExperience && (
        <Col xs={24} lg={4}>
          <Form.Item
            name={[contractorKey, 'person', 'experience']}
            label={t('driving_internship')}
            dependencies={experienceDependencies}
            rules={[
              required,
              ...(minExperience === undefined
                ? []
                : [
                    {
                      validator: (_: unknown, value: number) => {
                        if (value === undefined || value === null)
                          return Promise.resolve()
                        if (value < minExperience)
                          return Promise.reject(
                            new Error(
                              t('minimum_experience_required', {
                                number: minExperience
                              })
                            )
                          )
                        return Promise.resolve()
                      }
                    }
                  ])
            ]}
          >
            <InputParsedNumber
              className="w-full"
              placeholder={t('driving_internship')}
              min={1}
              suffix={t('years')}
              onChange={onChangeExperience}
              disabled={disableExperience}
            />
          </Form.Item>
        </Col>
      )}
    </Row>
  )
}
